import { Badge, Group, Paper, Stack, Text } from '@mantine/core';
import { IconClock } from '@tabler/icons-react';
import { useEffect, useMemo } from 'react';
import type { IntakeAnswers, IntakeAction, SessionType } from '../types';
import type { IntakeServiceCatalogEntry } from '../intakeServicesCatalog';

interface StepSessionTypeProps {
  answers: IntakeAnswers;
  dispatch: React.Dispatch<IntakeAction>;
  /**
   * Catalog entry for the service being booked this round. Gates which appointment types are
   * offered (per the EoC type's intakeType) and supplies the price of each. When omitted, both
   * options are shown without prices (safe fallback).
   */
  serviceCatalogEntry?: IntakeServiceCatalogEntry | null;
}

interface SessionOption {
  value: SessionType;
  label: string;
  duration: string;
  description: string;
}

const ALL_OPTIONS: SessionOption[] = [
  {
    value: 'intake',
    label: 'Intake session',
    duration: '50 minutes',
    description: 'A full introductory session with your provider to discuss your goals and begin treatment.',
  },
  {
    value: 'consultation',
    label: 'Consultation',
    duration: '15 minutes',
    description: 'A brief call to see if the provider is a good fit before scheduling a full session.',
  },
];

/** Which appointment types are allowed for this service's intake configuration. */
function allowedSessionTypes(entry: IntakeServiceCatalogEntry | null | undefined): SessionType[] {
  switch (entry?.intakeType) {
    case 'consultations':
      return ['consultation'];
    case 'intake-sessions':
      return ['intake'];
    default:
      return ['intake', 'consultation'];
  }
}

function priceLabel(cost: number | null | undefined): string {
  if (cost == null) return '';
  if (cost <= 0) return 'Free';
  return `$${Number.isInteger(cost) ? cost : cost.toFixed(2)}`;
}

export function StepSessionType({
  answers,
  dispatch,
  serviceCatalogEntry,
}: StepSessionTypeProps): JSX.Element {
  const allowed = useMemo(() => allowedSessionTypes(serviceCatalogEntry), [serviceCatalogEntry]);
  const options = ALL_OPTIONS.filter((o) => allowed.includes(o.value));
  const sessionType = answers.sessionType ?? allowed[0] ?? 'intake';

  // Persist a valid default for this round: when unset (reset between rounds) or not allowed for
  // this service's gating (e.g. consultation-only), select the first allowed option.
  useEffect(() => {
    if (answers.sessionType == null || !allowed.includes(answers.sessionType)) {
      dispatch({ type: 'SET_SESSION_TYPE', payload: allowed[0] ?? 'intake' });
    }
  }, [allowed, answers.sessionType, dispatch]);

  const costFor = (value: SessionType): number | null | undefined =>
    value === 'intake' ? serviceCatalogEntry?.sessions.intake?.cost : serviceCatalogEntry?.sessions.consultation?.cost;

  return (
    <Stack gap="md">
      {options.map((opt) => {
        const selected = sessionType === opt.value;
        const price = priceLabel(costFor(opt.value));
        return (
          <Paper
            key={opt.value}
            withBorder
            p="lg"
            radius="md"
            style={{
              cursor: 'pointer',
              borderColor: selected ? 'var(--mantine-color-gold-6)' : undefined,
              borderWidth: selected ? 2 : 1,
              background: selected ? 'var(--mantine-color-gold-0)' : undefined,
              transition: 'border-color 150ms ease, background 150ms ease',
            }}
            onClick={() => dispatch({ type: 'SET_SESSION_TYPE', payload: opt.value })}
          >
            <Group justify="space-between" align="flex-start" wrap="nowrap">
              <div>
                <Text fw={600} size="md">{opt.label}</Text>
                <Group gap={6} mt={2}>
                  <IconClock size={14} style={{ opacity: 0.5 }} />
                  <Text size="sm" c="dimmed">{opt.duration}</Text>
                </Group>
                <Text size="sm" c="dimmed" mt="xs">{opt.description}</Text>
              </div>
              {price && (
                <Badge
                  color={price === 'Free' ? 'green' : 'gold'}
                  variant="light"
                  size="lg"
                  style={{ flexShrink: 0 }}
                  styles={{ root: { maxWidth: 'none' }, label: { overflow: 'visible' } }}
                >
                  {price}
                </Badge>
              )}
            </Group>
          </Paper>
        );
      })}
    </Stack>
  );
}
